import { useMutation } from '@tanstack/react-query';
import { encodeAbiParameters, keccak256 } from 'viem';
import { signMessage } from 'viem/accounts';
import { z } from 'zod';
import { toast } from 'sonner';

import { getIsMigrationTradingBlocked } from '@/hooks/use-migration';
import { useAccountStore } from '@/store/use-account-store';
import {
  FilledOrder,
  NewOrder,
  useOrdersStore,
} from '@/store/use-orders-store';
import { COLLATERAL_DECIMALS, useMarketStore } from '@/store/use-markets-store';
import { ErrorCode, ErrorMsgs, OrderStatus, OrderType } from '@/types/enums';
import { ordersApi } from '@/lib/api-clients/rest-client';
import { MutationConfig } from '@/lib/api-clients/react-query';
import { getByNormalizedKey } from '@/utils/helpers';
import { abbrFromSym } from '@/utils/token-symbol';
import {
  bigIntToNum,
  formatBigInt,
  parseDecimalToBigInt,
} from '@/utils/value-format';
import { sendEvent } from '@/utils/analytics';
import { EventType } from '@/types';

export const TIF_OPTIONS = ['GTC', 'FOK', 'ALO', 'IOC'] as const;

export const createOrderInputSchema = z.object({
  symbol: z.string(),
  isBuy: z.boolean(),
  size: z
    .string()
    .regex(/^[0-9]\d*(\.\d+)?$/, 'Must be a positive number')
    .regex(/^(?!0*\.?0+$)/, 'Must not be zero or all zeros'),
  orderType: z.nativeEnum(OrderType),
  limitPrice: z
    .string()
    .regex(/^[0-9]\d*(\.\d+)?$/, 'Must be a positive number')
    .regex(/^(?!0*\.?0+$)/, 'Must not be zero or all zeros'),
  reduceOnly: z.boolean(),
  initMarginRatio: z.string().optional(),
  tpPrice: z
    .string()
    .optional()
    .or(
      z
        .string()
        .regex(/^[0-9]\d*(\.\d+)?$/, 'Must be a positive number')
        .regex(/^(?!0*\.?0+$)/, 'Must not be zero or all zeros'),
    ),
  slPrice: z
    .string()
    .optional()
    .or(
      z
        .string()
        .regex(/^[0-9]\d*(\.\d+)?$/, 'Must be a positive number')
        .regex(/^(?!0*\.?0+$)/, 'Must not be zero or all zeros'),
    ),
  tpSignature: z
    .string()
    .optional()
    .or(
      z
        .string()
        .regex(/^0[xX][0-9a-fA-F]+$/, 'Signature must be hexadecimal string'),
    ),
  slSignature: z
    .string()
    .optional()
    .or(
      z
        .string()
        .regex(/^0[xX][0-9a-fA-F]+$/, 'Signature must be hexadecimal string'),
    ),
  timeInForce: z.enum(TIF_OPTIONS).optional(),
});

export type CreateOrderInput = z.infer<typeof createOrderInputSchema>;
interface OrderBody {
  order: CreateOrderInput & { nonce: number; time: number };
  signature: string;
  recvWindow: number;
}

const createOrderBody = async (props: {
  order: CreateOrderInput;
  time: number;
  authSigningKey: `0x${string}`;
  nextNonce: () => number;
}): Promise<OrderBody> => {
  const { order, time, authSigningKey, nextNonce } = props;

  const nonce = nextNonce();

  const abiParamTypes = [
    { name: 'time', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'orderType', type: 'string' },
    { name: 'symbol', type: 'string' },
    { name: 'isBuy', type: 'bool' },
    { name: 'size', type: 'string' },
    { name: 'limitPrice', type: 'string' },
    { name: 'reduceOnly', type: 'bool' },
  ];

  const encodedOrder = encodeAbiParameters(abiParamTypes, [
    BigInt(time),
    BigInt(nonce),
    order.orderType,
    order.symbol,
    order.isBuy,
    order.size,
    order.limitPrice,
    order.reduceOnly ?? false, // TODO: dangerous?
  ]);

  const signature = await signMessage({
    message: { raw: keccak256(encodedOrder) },
    privateKey: authSigningKey,
  });

  // Helper function to generate signature for a price target
  const generatePriceSignature = async (
    orderType: string,
    targetPrice: string,
    nonce: number,
  ) => {
    const encodedTargetOrder = encodeAbiParameters(abiParamTypes, [
      BigInt(time),
      BigInt(nonce),
      orderType,
      order.symbol,
      !order.isBuy,
      order.size,
      targetPrice,
      true,
    ]);

    return signMessage({
      message: { raw: keccak256(encodedTargetOrder) },
      privateKey: authSigningKey,
    });
  };

  // Generate signatures for TP/SL if they exist
  // Signature depends on nonce for TP / SL.  To simplify coordination, we have the frontend and backend
  // treat TP nonce as original order nonce + 1 and SL nonce as original order nonce + 2
  const tpSignature = order.tpPrice
    ? await generatePriceSignature(
        OrderType.TAKE_PROFIT,
        order.tpPrice,
        nonce + 1,
      )
    : undefined;
  const slSignature = order.slPrice
    ? await generatePriceSignature(
        OrderType.STOP_LOSS,
        order.slPrice,
        nonce + 2,
      )
    : undefined;

  // Create a clean order object, removing tpPrice and slPrice
  const { tpPrice: _, slPrice: __, ...cleanOrder } = order;

  const body: OrderBody = {
    order: { ...cleanOrder, nonce, time },
    signature,
    recvWindow: 0,
  };

  // Only include TP/SL fields for LIMIT orders
  if (order.orderType === OrderType.LIMIT) {
    if (order.tpPrice) body.order.tpPrice = order.tpPrice;
    if (order.slPrice) body.order.slPrice = order.slPrice;
    if (tpSignature) body.order.tpSignature = tpSignature;
    if (slSignature) body.order.slSignature = slSignature;
    if (!body.order.timeInForce) body.order.timeInForce = 'GTC';
  }

  return body;
};

const waitForOrder = <T>(
  orderId: string,
  input: CreateOrderInput,
  time: number,
) => {
  return new Promise<T>((resolve, reject) => {
    const { address } = useAccountStore.getState();

    const successStatus =
      input.orderType === OrderType.MARKET
        ? OrderStatus.FILLED
        : OrderStatus.NEW;

    let subscriberTimeoutId: NodeJS.Timeout;
    let unsubscribe: () => void;

    const cleanup = () => {
      clearTimeout(subscriberTimeoutId);
      unsubscribe();
    };

    unsubscribe = useOrdersStore.subscribe((state) => {
      const { orders } = state.getOrders(address);
      const order = orders[orderId];

      if (!order) return;

      if (
        order.status === successStatus ||
        (order.orderType === OrderType.LIMIT &&
          order.status === OrderStatus.FILLED)
      ) {
        cleanup();
        resolve(order as T);
        return;
      }

      if (order.status === OrderStatus.REJECTED) {
        cleanup();
        reject(order.code as ErrorCode);
        return;
      }
    });

    subscriberTimeoutId = setTimeout(() => {
      cleanup();
      reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
    }, 30_000);

    useOrdersStore.getState().pollOrders([[orderId, successStatus, time]]);
  });
};

type CreateOrderPropsBase = {
  showToast: boolean;
  waitForConfirm?: boolean;
  isClose?: boolean;
};

export type CreateOrderRestResponse = {
  id: string;
  status: OrderStatus;
};

export type CreateScaleOrderRestResponse = {
  orderIds: string[];
  status: OrderStatus;
};

export type CreateOrderResponse =
  | CreateOrderRestResponse
  | FilledOrder
  | NewOrder;

export type CreateScaleOrderResponse =
  | CreateScaleOrderRestResponse
  | (FilledOrder | NewOrder)[];

export const createOrder = async (
  props: CreateOrderPropsBase & {
    order: CreateOrderInput;
  },
): Promise<CreateOrderResponse> => {
  const { order, showToast, waitForConfirm = false, isClose } = props;

  if (getIsMigrationTradingBlocked()) {
    throw ErrorCode.ACCOUNT_MIGRATION_LOCKED;
  }

  const { address, accounts, getAuthParams, nextNonce, getAccurateTime } =
    useAccountStore.getState();
  const time = getAccurateTime();
  const authParams = getAuthParams();

  if (!authParams?.signingKey) {
    console.error('No signing key found');
    throw new Error('No signing key found');
  }

  const body: OrderBody = await createOrderBody({
    order,
    time,
    authSigningKey: authParams.signingKey,
    nextNonce,
  });

  const promise: Promise<CreateOrderRestResponse> = ordersApi
    .post('/orders', body)
    .then((res) => {
      if (order.orderType !== OrderType.MARKET) return res;
      const { setOrder } = useOrdersStore.getState();
      const { marketSpec } = useMarketStore(order.symbol).getState();

      setOrder({
        id: res.id,
        symbol: order.symbol,
        isBuy: order.isBuy,
        orderType: order.orderType,
        status: OrderStatus.NEW,
        limitPrice: marketSpec
          ? parseDecimalToBigInt(order.limitPrice, marketSpec.priceDecimals)
          : 0n,
        size: marketSpec
          ? parseDecimalToBigInt(order.size, marketSpec.sizeDecimals)
          : 0n,
        tpPrice:
          order.tpPrice && marketSpec
            ? parseDecimalToBigInt(order.tpPrice, marketSpec.priceDecimals)
            : undefined,
        slPrice:
          order.slPrice && marketSpec
            ? parseDecimalToBigInt(order.slPrice, marketSpec.priceDecimals)
            : undefined,
        initMarginRatio: parseDecimalToBigInt(order.initMarginRatio, 4n),
        reduceOnly: order.reduceOnly ?? false,
        postTime: body.order.time,
      });

      return res;
    });

  const confirmPromise = promise.then<FilledOrder | NewOrder>((res) => {
    const { id: orderId } = res;

    sendEvent(EventType.ORDER_PLACED, {
      symbol: order.symbol,
      size: Number(order.size),
      stage: isClose ? 'close' : 'open',
    });

    return waitForOrder<FilledOrder | NewOrder>(orderId, order, time);
  });

  if (showToast) {
    const loadingId = toast.loading(
      order.orderType === OrderType.MARKET
        ? 'Submitting order...'
        : 'Adding order...',
    );

    confirmPromise
      .then((order) => {
        const { symbol } = order as NewOrder | FilledOrder;
        const { marketSpec } = useMarketStore(symbol).getState();
        const size =
          order.orderType === OrderType.MARKET
            ? (order as FilledOrder).lastFilledSize
            : order.size;
        const price =
          order.orderType === OrderType.MARKET
            ? (order as FilledOrder).lastFilledPrice
            : order.limitPrice;

        toast.success(
          `${order.orderType === OrderType.MARKET ? 'Market order executed' : 'Limit order added'} at ${formatBigInt(price, marketSpec.priceDecimals)} for ${formatBigInt(size, marketSpec.sizeDecimals)} ${abbrFromSym(symbol)}`,
          { id: loadingId },
        );
      })
      .catch((error: ErrorCode) => {
        if (error === ErrorCode.UNKNOWN) {
          toast.dismiss(loadingId);
          return;
        }
        if (error === ErrorCode.OI_CAP_EXCEEDED) {
          sendEvent(EventType.OI_CAP_EXCEEDED, {
            symbol: order.symbol,
            size: Number(order.size),
          });
        } else {
          const account = getByNormalizedKey(accounts, address);
          sendEvent(EventType.ORDER_REJECTED, {
            msg: ErrorMsgs[error],
            code: error,
            size: Number(order.size),
            isBuy: order.isBuy,
            ...(account
              ? {
                  collateral: bigIntToNum(
                    account.collateral,
                    COLLATERAL_DECIMALS,
                  ),
                }
              : {}),
          });
        }
        toast.error(`Order rejected: ${ErrorMsgs[error as ErrorCode]}`, {
          id: loadingId,
        });
      });
  }

  return waitForConfirm ? confirmPromise : promise;
};

export const createScaleOrder = async (
  props: CreateOrderPropsBase & {
    orders: CreateOrderInput[];
  },
): Promise<CreateScaleOrderResponse> => {
  const { orders, showToast, waitForConfirm = false } = props;

  if (getIsMigrationTradingBlocked()) {
    throw ErrorCode.ACCOUNT_MIGRATION_LOCKED;
  }

  const { address, accounts, getAuthParams, nextNonce, getAccurateTime } =
    useAccountStore.getState();
  const time = getAccurateTime();
  const authParams = getAuthParams();

  if (!authParams?.signingKey) {
    console.error('No signing key found');
    throw new Error('No signing key found');
  }

  const bodies: OrderBody[] = [];

  for (const order of orders) {
    bodies.push(
      await createOrderBody({
        order,
        time,
        authSigningKey: authParams.signingKey,
        nextNonce,
      }),
    );
  }

  const promise: Promise<CreateScaleOrderRestResponse> = ordersApi.post(
    '/orders/scale',
    { orders: bodies },
  );

  const size = orders.reduce((acc, curr) => acc + Number(curr.size), 0);

  const confirmPromise = promise.then<(FilledOrder | NewOrder)[]>((res) => {
    const { orderIds } = res;

    sendEvent(EventType.ORDER_PLACED_SCALE, {
      symbol: orders[0].symbol,
      size,
      amount: orders.length,
    });

    return Promise.all(
      orderIds.map((id, index) =>
        waitForOrder<FilledOrder | NewOrder>(id, orders[index], time),
      ),
    );
  });

  if (showToast) {
    const loadingId = toast.loading('Adding orders...');

    confirmPromise
      .then((results) => {
        const firstOrder = results[0];
        const lastOrder = results[results.length - 1];
        const size = results.reduce((acc, o) => acc + o.size, 0n);
        const { marketSpec } = useMarketStore(firstOrder.symbol).getState();

        toast.success(
          `Placed ${results.length} orders totaling ${formatBigInt(size, marketSpec.sizeDecimals)} ${abbrFromSym(firstOrder.symbol)} from ${formatBigInt(firstOrder.limitPrice, marketSpec.priceDecimals)} to ${formatBigInt(lastOrder.limitPrice, marketSpec.priceDecimals)}`,
          { id: loadingId },
        );
      })
      .catch((error: ErrorCode) => {
        if (error === ErrorCode.UNKNOWN) {
          toast.dismiss(loadingId);
          return;
        }
        if (error === ErrorCode.OI_CAP_EXCEEDED) {
          sendEvent(EventType.OI_CAP_EXCEEDED, {
            symbol: orders[0].symbol,
            size,
          });
        } else {
          const account = getByNormalizedKey(accounts, address);
          sendEvent(EventType.ORDER_REJECTED, {
            msg: ErrorMsgs[error],
            code: error,
            size,
            isBuy: orders[0].isBuy,
            ...(account
              ? {
                  collateral: bigIntToNum(
                    account.collateral,
                    COLLATERAL_DECIMALS,
                  ),
                }
              : {}),
          });
        }
        toast.error(`Order rejected: ${ErrorMsgs[error]}`, {
          id: loadingId,
        });
      });
  }

  return waitForConfirm ? confirmPromise : promise;
};

type UseCreateOrderOptions = {
  mutationConfig?: MutationConfig<typeof createOrder>;
};

export const useCreateOrder = ({
  mutationConfig = {},
}: UseCreateOrderOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: (params) => createOrder(params),
  });
};

type UseCreateScaleOrderOptions = {
  mutationConfig?: MutationConfig<typeof createScaleOrder>;
};

export const useCreateScaleOrder = ({
  mutationConfig = {},
}: UseCreateScaleOrderOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: (params) => createScaleOrder(params),
  });
};
